import React, { useState } from 'react';
import { AdminEmailEditor } from './AdminEmailEditor';
import {
  Autocomplete,
  Button,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';

export interface CreateField {
  name: string;
  label: string;
  type?: 'text' | 'textarea' | 'select' | 'autocomplete' | 'email';
  htmlName?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  placeholder?: string;
}

interface CreateRecordModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  fields: CreateField[];
  submitLabel?: string;
  isSubmitting?: boolean;
  error?: string | null;
  onSubmit: (values: Record<string, string>) => void;
  renderDetails?: (values: Record<string, string>) => React.ReactNode;
}

/**
 * A small form for adding a record by hand.
 *
 * One component for both the CRM and the development board: the fields differ
 * but "a modal with a few inputs and a save button" does not, and two copies
 * would drift the first time one gained validation.
 */
export const CreateRecordModal: React.FC<CreateRecordModalProps> = ({
  open,
  onClose,
  title,
  fields,
  submitLabel = 'Create',
  isSubmitting,
  error,
  onSubmit,
  renderDetails,
}) => {
  const [values, setValues] = useState<Record<string, string>>({});

  const set = (name: string, value: string) => setValues((prev) => ({ ...prev, [name]: value }));
  const missing = fields.filter((f) => f.required && !(values[f.name] ?? '').trim());

  const close = () => {
    setValues({});
    onClose();
  };

  return (
    <Modal open={open} onClose={close}>
      <ModalDialog sx={{ width: { xs: 'calc(100vw - 24px)', sm: renderDetails ? 640 : 460 }, maxWidth: '100%', maxHeight: '85dvh', overflow: 'auto' }}>
        <ModalClose />
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {fields.map((field) => (
              <FormControl key={field.name}>
                <FormLabel>{field.label}</FormLabel>
                {field.type === 'email' ? (
                  <AdminEmailEditor label={field.label} text={values[field.name] ?? ''}
                    html={values[field.htmlName ?? 'body_html'] ?? ''} disabled={isSubmitting}
                    onChange={(html, text) => setValues(previous => ({ ...previous, [field.name]: text, [field.htmlName ?? 'body_html']: html }))} />
                ) : field.type === 'textarea' ? (
                  <Textarea
                    size="sm"
                    minRows={3}
                    placeholder={field.placeholder}
                    value={values[field.name] ?? ''}
                    onChange={(event) => set(field.name, event.target.value)}
                  />
                ) : field.type === 'autocomplete' ? (
                  <Autocomplete
                    size="sm"
                    options={(field.options ?? []).map(option => option.value)}
                    getOptionLabel={value => field.options?.find(option => option.value === value)?.label ?? value}
                    placeholder={field.placeholder}
                    value={values[field.name] || null}
                    onChange={(_, value) => set(field.name, value ?? '')}
                  />
                ) : field.type === 'select' ? (
                  <Select
                    size="sm"
                    value={values[field.name] ?? field.options?.[0]?.value ?? ''}
                    onChange={(_, value) => value && set(field.name, value as string)}
                  >
                    {(field.options ?? []).map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    size="sm"
                    placeholder={field.placeholder}
                    value={values[field.name] ?? ''}
                    onChange={(event) => set(field.name, event.target.value)}
                  />
                )}
              </FormControl>
            ))}

            {renderDetails?.(values)}

            {error && (
              <Typography level="body-xs" color="danger">
                {error}
              </Typography>
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="sm" variant="plain" color="neutral" onClick={close}>
                Cancel
              </Button>
              <Button
                size="sm"
                loading={isSubmitting}
                disabled={missing.length > 0}
                onClick={() => {
                  // Defaults applied here rather than in state, so an untouched
                  // select still submits its first option.
                  const payload = { ...values };
                  fields.forEach((field) => {
                    if (field.type === 'select' && !payload[field.name]) {
                      payload[field.name] = field.options?.[0]?.value ?? '';
                    }
                  });
                  onSubmit(payload);
                }}
              >
                {submitLabel}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};

export default CreateRecordModal;
